import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Feature } from 'geojson';
import { useParams } from 'react-router-dom';
import {
    useIsAdmin,
    usePathfinderAisData,
    usePathfinderData,
    usePathfinderFilesExists,
    usePFMetadataQuery,
} from '../../../requests/reports';
import PathfinderMap from '../../../shared/components/PathfinderMap';
import { PathfinderErrorHandler } from '../../../utils/errorHandling';
import { PathfinderLeftSidebar } from '../components/PathfinderLeftSidebar';
import { PathfinderList } from '../components/PathfinderList';

import { Legend, TrackComplete, TrackGap } from '@vake/ui-kit';
import constants from '../../../shared/constants';
import { isValidDateString } from '../../../utils/date';
import { PathfinderSlider } from '../../../utils/Slider';
import { Button } from '@vake/ui-kit';
import { Flag, MagnifyingGlass, X } from '@phosphor-icons/react';
import FlagChangeButton from '../../../shared/components/FlagChangeButton';
import { useAuth } from '../../../auth/AuthProvider';

export interface IChosenBoatObject {
    lon?: number;
    lat?: number;
    timestamp?: string;
    image_path?: number;
    id?: number | string;
}

export const PathfinderReport: FC = () => {
    const params = useParams();
    const [mmsi, setMmsi] = useState<string | null>(null);
    const { data: reportMetadata, isSuccess: metaDataIsSuccess } = usePFMetadataQuery(
        params.id ? params.id : null,
        mmsi
    );
    const { data: pathfinderData, isLoading } = usePathfinderData(params);
    const { data: pathfinderFilesExists } = usePathfinderFilesExists(params);
    const { data: pathfinderAisData } = usePathfinderAisData(params);
    const { token } = useAuth();
    const { data: isAdmin } = useIsAdmin(token);

    const containerRef = useRef<HTMLDivElement>(null);
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [chosenBoatObject, setChosenBoatObject] = useState<IChosenBoatObject | null>(null);
    const [hoveredPathElement, setHoveredPathElement] = useState<number | null>(null);
    const [boatMetaData, setBoatMetaData] = useState<Feature | null>(null);
    const [reportName, setReportName] = useState<string | null>(null);
    const [shipImo, setShipImo] = useState<string | null>(null);
    const [havePlanet, setHavePlanet] = useState<boolean>(false);
    const [hasOilPipelines, setHasOilPipelines] = useState<boolean>(false);
    const [filterStartDate, setFilterStartDate] = useState<number>(-1);
    const [filterEndDate, setFilterEndDate] = useState<number>(-1);
    const [sliderMinDate, setSliderMinDate] = useState<number>(-1);
    const [sliderMaxDate, setSliderMaxDate] = useState<number>(-1);
    const [enableGetFlagDetection, setEnableGetFlagDetection] = useState<boolean>(false);
    const handleFlagChangePress = () => {
        setEnableGetFlagDetection(!enableGetFlagDetection);
    };

    if (metaDataIsSuccess && reportMetadata && reportName == null && shipImo == null) {
        if (reportMetadata.reportName) {
            setReportName(reportMetadata.reportName);
            document.title = reportMetadata.reportName;
        }
        if (reportMetadata.imo) {
            setShipImo(reportMetadata.imo);
        }
    }

    const firstLastAISMessage: FlagChangeProps[] | null = useMemo(() => {
        if (pathfinderAisData && pathfinderAisData.features?.length > 0) {
            const feat = pathfinderAisData.features;
            const firstLastAISMessage: FlagChangeProps[] = [];
            const firstMessage = feat[0];
            if (firstMessage.properties?.dt_pos_utc && firstMessage.geometry.type === 'Point') {
                firstLastAISMessage.push({
                    lat: firstMessage.geometry.coordinates[0],
                    lon: firstMessage.geometry.coordinates[1],
                    timestamp: new Date(firstMessage.properties.dt_pos_utc),
                });
            }
            const lastMessage = feat[feat.length - 1];
            if (lastMessage.properties?.dt_pos_utc && lastMessage.geometry.type === 'Point') {
                firstLastAISMessage.push({
                    lat: lastMessage.geometry.coordinates[0],
                    lon: lastMessage.geometry.coordinates[1],
                    timestamp: new Date(lastMessage.properties.dt_pos_utc),
                });
            }
            if (firstLastAISMessage.length === 2) return firstLastAISMessage;
        }
        return null;
    }, [pathfinderAisData]);

    // Setting report params such as mmsi, timestamps and name (if exists)
    useEffect(() => {
        const incomingParameters = typeof params['id'] !== 'undefined' ? params['id'].split('_') : [];
        if (incomingParameters.length >= 3) {
            setMmsi(incomingParameters[0]);
            const from = incomingParameters[1].split('T')[0];
            const to = incomingParameters[2].split('T')[0];
            setFromDate(from);
            setToDate(to);
            if (document.title === 'Vake') {
                // Vake is the default title, only change to mmsi for default case.
                document.title = incomingParameters[0];
            }
            if (isValidDateString(from) && isValidDateString(to)) {
                //Adding buffer ensuring timeslider does not remove any elements at the sides
                const minDate = new Date(from).getTime() - 100000000;
                const maxDate = new Date(to).getTime() + 100000000;
                setSliderMinDate(minDate);
                setSliderMaxDate(maxDate);
                setFilterStartDate(minDate);
                setFilterEndDate(maxDate);
            }
        } else if (incomingParameters.length === 1) {
            if (typeof incomingParameters[0] === 'string') {
                const name = incomingParameters[0].charAt(0).toUpperCase() + incomingParameters[0].slice(1);
                setReportName(name);
            }
        }
    }, [params]);

    useEffect(() => {
        if (pathfinderData) {
            if (pathfinderData.ais_positions && pathfinderData.ais_positions.features) {
                pathfinderData.ais_positions.features.forEach((element) => {
                    if (!havePlanet && element.properties?.image_source === 'planet_scope') {
                        setHavePlanet(true);
                    }
                });
            }
            if (pathfinderData.oil_pipelines) {
                setHasOilPipelines(true);
            }
        }
    }, [pathfinderData, isLoading, havePlanet]);
    useEffect(() => window.scrollTo(0, 0), []);
    return (
        <div>
            {params ? (
                <>
                    <PathfinderLeftSidebar
                        reportName={reportName}
                        mmsi={mmsi}
                        shipImo={shipImo}
                        fromDate={fromDate}
                        toDate={toDate}
                        havePlanet={havePlanet}
                        hasOilPipelines={hasOilPipelines}
                        firstLastAISMessage={firstLastAISMessage}
                    />
                    {pathfinderData && (pathfinderData.ais_positions || pathfinderData.ais_tracks) ? (
                        <>
                            {pathfinderData.ais_positions ? (
                                <PathfinderList
                                    aisPositions={pathfinderData?.ais_positions}
                                    chosenBoatObject={chosenBoatObject}
                                    boatMetaData={boatMetaData}
                                    setBoatMetaData={setBoatMetaData}
                                    setHoveredPathElement={setHoveredPathElement}
                                    mmsi={mmsi}
                                    reportName={reportName}
                                />
                            ) : null}
                            {filterStartDate !== -1 &&
                            filterEndDate !== -1 &&
                            sliderMinDate !== -1 &&
                            sliderMaxDate !== -1 ? (
                                <PathfinderSlider
                                    minDate={sliderMinDate}
                                    maxDate={sliderMaxDate}
                                    setFilterStartDate={setFilterStartDate}
                                    setFilterEndDate={setFilterEndDate}
                                />
                            ) : null}
                        </>
                    ) : null}
                    <PathfinderErrorHandler
                        hasAIS={pathfinderFilesExists?.has_ais}
                        hasImages={pathfinderFilesExists?.has_images}
                        createdAt={pathfinderFilesExists?.report_created_at}
                        isLoading={isLoading}
                    />
                </>
            ) : null}
            <PathfinderMap
                containerRef={containerRef}
                pathfinderData={pathfinderData}
                pathfinderAisData={pathfinderAisData}
                boatMetaData={boatMetaData}
                setChosenBoatObject={setChosenBoatObject}
                hoveredPathElement={hoveredPathElement}
                filterStartDate={filterStartDate}
                filterEndDate={filterEndDate}
            />
            <span className="absolute bottom-[70px] left-2">
                <Legend
                    legendData={[
                        {
                            title: 'Ship tracks',
                            items: [
                                {
                                    label: 'AIS track',
                                    icon: <TrackComplete size={20} color={constants.colors.pallet.purple} />,
                                    tooltip: 'Continuous AIS signal',
                                },
                                {
                                    label: 'AIS track gap',
                                    icon: <TrackGap size={20} color={'#e7a86c'} />,
                                    tooltip:
                                        'Gap in AIS signals longer than 30 minutes.\n\nLoss of coverage or deliberately going dark is typical causes.',
                                },
                                {
                                    label: 'AIS track gap w/ image',
                                    icon: <TrackGap size={20} color={'#ec8e8e'} />,
                                    tooltip:
                                        'Gap in AIS signals, with satellite pass, enabling detection of dark vessls',
                                },
                            ],
                        },
                    ]}
                />
                {isAdmin && (
                    <div>
                        {/* className="absolute bottom-[5vh] left-1 z-10 "> */}
                        {enableGetFlagDetection ? (
                            <FlagChangeButton mmsi={mmsi} timePoint={firstLastAISMessage} />
                        ) : null}
                        <Button
                            iconBefore={<Flag size={18} />}
                            text="Detect Flag Change"
                            size="xsmall"
                            variant="neutral"
                            iconAfter={enableGetFlagDetection ? <X size={18} /> : <MagnifyingGlass size={18} />}
                            onClick={handleFlagChangePress}
                        />
                    </div>
                )}
            </span>
        </div>
    );
};
